// hooks/api/techProgram.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as techProgramService from "@/services/techProgram.service";
import {
  TechProgramCreateType,
  TechProgramParamsType,
  TechProgramType,
} from "@/services/techProgram.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const techProgramsKey = {
  all: ["techPrograms"] as const,
  lists: (filters: TechProgramParamsType) =>
    [...techProgramsKey.all, "lists", filters] as const,
  detail: (id: string) => [...techProgramsKey.all, "detail", id] as const,
};

export const useTechProgramsQuery = (filters: TechProgramParamsType) =>
  useQuery({
    queryKey: techProgramsKey.lists(filters),
    queryFn: () => techProgramService.fetchAllTechPrograms(filters),
    placeholderData: (prev) => prev,
  });

export const useTechProgramByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: techProgramsKey.detail(id),
    queryFn: () => techProgramService.fetchTechProgramById(id),
    enabled: enabled && !!id,
  });

export const useCreateTechProgramMutation = (
  filters: TechProgramParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation<TechProgramType, AxiosError, TechProgramCreateType>({
    mutationFn: (data) => techProgramService.createTechProgram(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: techProgramsKey.lists(filters),
      });
      notifySuccess("Program created successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to create program"),
  });
};

export const useUpdateTechProgramMutation = (
  filters: TechProgramParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ _id, data }: { _id: string; data: TechProgramCreateType }) =>
      techProgramService.updateTechProgram(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: techProgramsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: techProgramsKey.detail(variables._id),
      });
      notifySuccess("Program updated successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to update program"),
  });
};

export const useDeleteTechProgramMutation = (
  filters: TechProgramParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) => techProgramService.deleteTechProgram(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: techProgramsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: techProgramsKey.detail(_id) });
      notifySuccess("Program deleted successfully");
    },
    onError: (error: any) =>
      notifyError(error?.response?.data?.message || "Failed to delete program"),
  });
};
